/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Loader } from '../../../../../../../../common/react/components/Loader';
import { getTranslation } from '../../../../../../../../common/react/utils/translations';
import {
    performanceChartDataTypeSelector,
    performanceChartNavSelector,
    performanceChartDataSelector
} from '../../../../../../store/js/modules/statspro/performance-chart/selectors';
import { FETCH_PERFORMANCE_CHART_STATS } from '../../../../../../store/js/modules/statspro/performance-chart/actions';
import { STATS_CATEGORY_IDS } from '../../../common/config/performance-stats';
import { EmptyState } from '../../../../../../../../common/react/components/EmptyState';

import {
    ComparisonPlayerImages,
    PerformanceChartBarChart,
    PerformanceChartLineChart
} from './components';
import { performanceChartPlayerTwoDataSelector } from '../../../../../../store/js/modules/statspro/performance-chart/selectors/player-two-data-selector';
import { useWindowWidth } from '../../../../../../../../common/react/hooks/use-window-resize';
import { composeCareerData } from '../../utils';
import { composePlayerTwoCareerData } from '../../utils/compose-player-two-career-data';
import { composeSeasonBarData } from '../../utils/compose-season-bar-data';
import { composePlayerTwoSeasonBarData } from '../../utils/compose-player-two-season-bar-data';
import { composeSeasonLineData } from '../../utils/compose-season-line-data';
import { composePlayerTwoSeasonLineData } from '../../utils/compose-player-two-season-line-data';

export const PerformanceChartContainer = ({
    playerIds,
    type,
    containerRef
}) => {
    const dispatch = useDispatch();
    const playerData = useSelector(performanceChartDataSelector);
    const playerTwoData = useSelector(performanceChartPlayerTwoDataSelector);
    const nav = useSelector(performanceChartNavSelector);
    const dataType = useSelector(performanceChartDataTypeSelector);
    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );
    const season = seasons.find((item) => item.id === nav.season.id);
    const seasonId = season?.providerId ?? '';
    const playerDetails = useSelector((state) =>
        PULSE.app.redux.selectors.statsplayerprofile.item(state, playerIds[1])
    );
    const playerTwoDetails = useSelector((state) =>
        PULSE.app.redux.selectors.statsplayerprofile.item(state, playerIds[2])
    );

    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [chartType, setChartType] = useState('bar');
    const [xLabel, setXLabel] = useState(getTranslation('label.stats.season'));
    const [yLabel, setYLabel] = useState(
        getTranslation(
            'label.performanceChart.' + STATS_CATEGORY_IDS[nav.category]
        )
    );
    const [hideTooltip, setHideTooltip] = useState(false);
    const windowWidth = useWindowWidth(500);

    // Fetch career stats
    useEffect(() => {
        // reset data before fetching
        dispatch(
            FETCH_PERFORMANCE_CHART_STATS.request({
                type: 'season',
                index: 1,
                reset: true
            })
        );

        dispatch(
            FETCH_PERFORMANCE_CHART_STATS.request({
                type: 'career',
                playerId: playerIds[1],
                index: 1
            })
        );
    }, [playerIds[1]]);

    useEffect(() => {
        // reset data before fetching
        dispatch(
            FETCH_PERFORMANCE_CHART_STATS.request({
                type: 'season',
                index: 2,
                reset: true
            })
        );

        dispatch(
            FETCH_PERFORMANCE_CHART_STATS.request({
                type: 'career',
                playerId: playerIds[2],
                index: 2
            })
        );
    }, [playerIds[2]]);

    useEffect(() => {
        // Initialise the player profile in the redux store
        dispatch(
            PULSE.app.redux.actions.statsplayerprofile.ADD_PLAYER_PROFILE.request(
                { seasonId, playerId: playerIds[1] }
            )
        );

        if (playerIds[2]) {
            dispatch(
                PULSE.app.redux.actions.statsplayerprofile.ADD_PLAYER_PROFILE.request(
                    { seasonId, playerId: playerIds[2] }
                )
            );
        }
    }, [season]);

    useEffect(() => {
        // update Stats Category label
        setYLabel(
            getTranslation(
                'label.performanceChart.' + STATS_CATEGORY_IDS[nav.category]
            )
        );
    }, [nav.category]);

    useEffect(() => {
        setNoDataAvailable(false);
        setXLabel(getTranslation('label.stats.season'));

        let composedChartData = [];

        // If Career is chosen we display..
        // Totals - Totals for each season
        // Per Game - N/A
        if (nav.season.id === -1) {
            if (!playerData.yearly?.length && !playerTwoData.yearly?.length) {
                setChartData([]);
                return;
            }

            // Error handling for when there is no data
            if (
                playerIds[1] &&
                playerData.yearly?.[0] === 'no data available'
            ) {
                if (
                    !playerIds[2] ||
                    playerTwoData.yearly?.[0] === 'no data available'
                ) {
                    setNoDataAvailable(true);
                    return;
                }
            }

            if (dataType.type === 'totals') {
                setChartType('bar');

                composedChartData = composeCareerData(playerData, nav);

                // If there is data for player two, combine the data for both players
                composedChartData = composePlayerTwoCareerData(
                    composedChartData,
                    playerTwoData,
                    nav
                );

                setChartData(composedChartData);
            }
        }
        // If Season is chosen we display..
        // Per Game - Individual totals for each round in the season
        // Totals - Cumulative totals for each round in the season
        else if (nav.season.id && nav.season.id !== -1) {
            setXLabel(getTranslation('label.match.round'));

            // Season stats
            if (!playerData.season[season.providerId] && playerIds[1]) {
                // Fetch for rounds data if it doesn't aleady exist
                setChartData([]);
                dispatch(
                    FETCH_PERFORMANCE_CHART_STATS.request({
                        type: 'season',
                        playerId: playerIds[1],
                        seasonId: season.providerId,
                        index: 1
                    })
                );
            }

            if (!playerTwoData.season[season.providerId] && playerIds[2]) {
                // Fetch for rounds data if it doesn't aleady exist
                setChartData([]);
                dispatch(
                    FETCH_PERFORMANCE_CHART_STATS.request({
                        type: 'season',
                        playerId: playerIds[2],
                        seasonId: season.providerId,
                        index: 2
                    })
                );
            }

            // Error handling for when there is no data
            if (
                playerIds[1] &&
                playerData.season[season.providerId]?.[0]?.error
            ) {
                if (
                    !playerIds[2] ||
                    playerTwoData.season[season.providerId]?.[0]?.error
                ) {
                    setNoDataAvailable(true);
                    return;
                }
            }

            let seasonArray = [];
            if (playerData.season[season.providerId]) {
                seasonArray = [
                    ...playerData.season[season.providerId]
                ].reverse();
            }

            let playerTwoSeasonArray = [];
            if (playerTwoData.season[season.providerId]) {
                playerTwoSeasonArray = [
                    ...playerTwoData.season[season.providerId]
                ].reverse();
            }

            if (dataType.type === 'perGame') {
                setChartType('bar');

                composedChartData = composeSeasonBarData(
                    seasonArray,
                    nav,
                    type
                );

                // If there is data for player two, combine the data for both players
                composedChartData = composePlayerTwoSeasonBarData(
                    composedChartData,
                    playerTwoSeasonArray,
                    nav
                );

                setChartData(composedChartData.sort((a, b) => a.name - b.name));
            } else if (dataType.type === 'totals') {
                setChartType('line');

                composedChartData = composeSeasonLineData(
                    seasonArray,
                    nav,
                    type
                );

                // If there is data for player two, combine the data for both players
                composedChartData = composePlayerTwoSeasonLineData(
                    composedChartData,
                    playerTwoSeasonArray,
                    seasonArray,
                    nav
                );

                setChartData(composedChartData);
            }
        }
    }, [
        playerData.yearly,
        playerData.season,
        playerTwoData.yearly,
        playerTwoData.season,
        nav.season.id,
        dataType.type,
        nav.category
    ]);

    // Listen for window reisize in case the tooltip is open as it can cause layout issues
    // when changing the orientation
    useEffect(() => {
        // The buffer is to account for padding on desktop
        const buffer = 100;
        if (
            containerRef.current &&
            innerWidth > containerRef.current.offsetWidth + buffer
        ) {
            setHideTooltip(true);
        } else {
            setHideTooltip(false);
        }
    }, [windowWidth]);

    return (
        <div className="performance-chart__content">
            {(() => {
                if (!chartData.length && !noDataAvailable) {
                    // Loader for waiting for data
                    return (
                        <div className="performance-chart__loader">
                            <Loader />
                        </div>
                    );
                } else if (noDataAvailable) {
                    // Error handling for when there is no data
                    return (
                        <div className="performance-chart__empty-state">
                            <EmptyState
                                titleTranslation="label.stats.error.noStatsAvailable"
                                summaryTranslation="label.stats.error.noStatsAvailable.summary"
                            />
                        </div>
                    );
                }

                if (chartData.length > 0) {
                    return (
                        <>
                            {type === 'comparison' && (
                                <ComparisonPlayerImages
                                    players={[playerDetails, playerTwoDetails]}
                                />
                            )}
                            {chartType === 'bar' && (
                                <PerformanceChartBarChart
                                    data={chartData}
                                    xLabel={xLabel}
                                    yLabel={yLabel}
                                    playerDetails={playerDetails}
                                    playerTwoDetails={playerTwoDetails}
                                    type={type}
                                    hideTooltip={hideTooltip}
                                />
                            )}
                            {chartType === 'line' && (
                                <PerformanceChartLineChart
                                    data={chartData}
                                    xLabel={xLabel}
                                    yLabel={yLabel}
                                    playerDetails={playerDetails}
                                    playerTwoDetails={playerTwoDetails}
                                    type={type}
                                    hideTooltip={hideTooltip}
                                />
                            )}
                        </>
                    );
                }
            })()}
        </div>
    );
};

PerformanceChartContainer.propTypes = {
    playerIds: PropTypes.object.isRequired,
    type: PropTypes.string,
    containerRef: PropTypes.any
};
